import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { Table, message } from 'antd';
import { DateTime } from 'luxon';
import { getWorkReportEntryBatches } from '../../abWorkReporting/state/abWorkReporting.actions';
import { getIdMap } from '../../helpers/helpers';
import { downloadFile } from '../../files/fileHelpers';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import { decorateFile } from '../../forms/formHelpers';

const getColumns = (userMap) => [
  {
    title: 'Batch Number',
    dataIndex: 'batchNumber',
    key: 'batchNumber',
  },
  {
    title: 'Created By',
    dataIndex: 'createdBy',
    key: 'createdBy',
    render: (id) => userMap[id]?.name || 'Unknown User',
  },
  {
    title: 'Created At',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (timestamp) => (
      timestamp
        ? DateTime.fromMillis(timestamp).toLocaleString(DateTime.DATETIME_MED)
        : ''
    ),
  },
  {
    title: 'Worksheet Count',
    dataIndex: 'worksheetCount',
    key: 'worksheetCount',
  },
];

const worksheetColumns = [
  {
    title: 'Worksheet Number',
    dataIndex: 'number',
    key: 'number',
  },
  {
    title: 'Worksheet Status',
    dataIndex: 'status',
    key: 'status',
  },
  {
    title: 'Worksheet Work Date',
    dataIndex: 'workDate',
    key: 'workDate',
  },
];

export default function ABExportBatchTable() {
  const dispatch = useDispatch();
  const batches = useSelector((state) => state.abWorkReporting.batches);
  const users = useSelector((state) => state.users.users);

  const userMap = useMemo(() => getIdMap(users), [users]);

  useEffect(() => {
    dispatch(getWorkReportEntryBatches());
  }, []);

  const onRowClick = useCallback(async (record) => {
    if (!record.files?.length) {
      message.info('No files associated with this batch.');
      return;
    }

    return CustomConfirmModal({
      title: 'Download File',
      content: 'Are you sure you want to download the file?',
      onOk: async () => {
        const relevantFile = record.files[0];
        try {
          const { data: signedUrl } = await axios.get(`download/file/${relevantFile.fileId}`);
          if (signedUrl) {
            const fileObject = await decorateFile({ url: signedUrl, name: relevantFile.fileName }, relevantFile.fileId);
            if (fileObject) {
              downloadFile({ fileObject });
            }
          } else {
            message.error('Could not get download link for this file.');
          }
        } catch (err) {
          message.error('Failed to download file. Please try again.');
        }
      },
    })
  }, []);

  const exapndedTable = useCallback((record) => (
    <Table
      dataSource={record.worksheets}
      columns={worksheetColumns}
      rowKey="id"
      pagination={false}
    />
  ), []);

  return (
    <Table
      dataSource={batches}
      columns={getColumns(userMap)}
      rowKey="id"
      onRow={(record) => ({
        onClick: () => onRowClick(record),
        style: { cursor: record.files?.length > 0 ? 'pointer' : 'default' },
      })}
      expandable={{
        defaultExpandAllRows: true,
        expandable: (record) => record.worksheets?.length > 0,
        expandedRowKeys: batches.map((batch) => batch.id),
        expandedRowRender: exapndedTable,
      }}
    />
  );
}
